import { area, line } from '@visx/shape';
import { useMemo } from 'react';
import { useChart } from './context';
import { END_LABEL_GAP, END_LABEL_KEY, spreadLabels, stackSigned, type YearBucket } from './math';

/** Indexes of the visible window, with one extra point on each side so lines reach the edges. */
function useVisibleIndices(length: number): number[] {
  const { range } = useChart();
  const from = Math.max(0, range[0] - 1);
  const to = Math.min(length - 1, range[1] + 1);
  return useMemo(
    () => Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => from + i),
    [from, to],
  );
}

export interface LineLayerProps {
  /** Non-finite values (NaN) leave a gap. */
  values: ArrayLike<number>;
  /** CSS colour, typically `var(--chart-series-N)`. */
  color: string;
  width?: number;
  dashed?: boolean;
  /** De-emphasised (e.g. while another series is focused). */
  muted?: boolean;
}

export function LineLayer({
  values,
  color,
  width = 2,
  dashed = false,
  muted = false,
}: LineLayerProps) {
  const { dates, xScale, yScale, hoverIndex } = useChart();
  const indices = useVisibleIndices(values.length);
  const d = useMemo(
    () =>
      line<number>({
        x: (i) => xScale(dates[i] as Date),
        y: (i) => yScale(values[i] as number),
        defined: (i) => Number.isFinite(values[i]),
      })(indices) ?? '',
    [dates, xScale, yScale, values, indices],
  );
  const raw = hoverIndex >= 0 ? values[hoverIndex] : undefined;
  const hoverValue = raw !== undefined && Number.isFinite(raw) ? raw : undefined;
  return (
    <g aria-hidden style={{ opacity: muted ? 0.3 : 1, transition: 'opacity 120ms ease-out' }}>
      <path
        d={d}
        fill="none"
        style={{ stroke: color }}
        strokeWidth={width}
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeDasharray={dashed ? '4 4' : undefined}
      />
      {hoverValue !== undefined && !muted && (
        <circle
          cx={xScale(dates[hoverIndex] as Date)}
          cy={yScale(hoverValue)}
          r={4}
          style={{ fill: color, stroke: 'var(--chart-surface)' }}
          strokeWidth={2}
        />
      )}
    </g>
  );
}

export interface NegativeShadeProps {
  values: ArrayLike<number>;
  color?: string;
}

/** Shades the area between zero and the series wherever the series is negative. */
export function NegativeShade({ values, color = 'var(--status-critical)' }: NegativeShadeProps) {
  const { dates, xScale, yScale } = useChart();
  const indices = useVisibleIndices(values.length);
  const d = useMemo(
    () =>
      area<number>({
        x: (i) => xScale(dates[i] as Date),
        y0: () => yScale(0),
        y1: (i) => yScale(Math.min(0, values[i] as number)),
      })(indices) ?? '',
    [dates, xScale, yScale, values, indices],
  );
  return <path aria-hidden d={d} style={{ fill: color }} fillOpacity={0.16} />;
}

/** Emphasised zero baseline (only drawn when zero is inside the domain). */
export function ZeroLine() {
  const { yScale, innerWidth } = useChart();
  const [lo, hi] = yScale.domain() as [number, number];
  if (lo > 0 || hi < 0) return null;
  const y = yScale(0);
  return (
    <line
      aria-hidden
      x2={innerWidth}
      y1={y}
      y2={y}
      style={{ stroke: 'var(--chart-axis)' }}
      strokeWidth={1}
      shapeRendering="crispEdges"
    />
  );
}

export interface StackedSeries {
  id: string;
  values: ArrayLike<number>;
  color: string;
  muted?: boolean;
}

/**
 * Signed stacked areas: positive values stack up from zero, negative values stack down (e.g.
 * assets above, liabilities below). A thin surface-coloured stroke separates the bands.
 */
export function StackedAreaLayer({ series }: { series: readonly StackedSeries[] }) {
  const { dates, xScale, yScale } = useChart();
  const length = dates.length;
  const indices = useVisibleIndices(length);
  const bands = useMemo(
    () =>
      stackSigned(
        series.map((s) => s.values),
        length,
      ),
    [series, length],
  );
  const paths = useMemo(
    () =>
      bands.map(
        ({ y0, y1 }) =>
          area<number>({
            x: (i) => xScale(dates[i] as Date),
            y0: (i) => yScale(y0[i] ?? 0),
            y1: (i) => yScale(y1[i] ?? 0),
          })(indices) ?? '',
      ),
    [bands, dates, xScale, yScale, indices],
  );
  return (
    <g aria-hidden>
      {series.map((s, i) => (
        <path
          key={s.id}
          d={paths[i]}
          style={{ fill: s.color, stroke: 'var(--chart-surface)', opacity: s.muted ? 0.25 : 0.85 }}
          strokeWidth={0.75}
        />
      ))}
    </g>
  );
}

export interface BarSeries {
  id: string;
  /** One value per bucket. */
  values: ArrayLike<number>;
  color: string;
  muted?: boolean;
}

export interface BarLayerProps {
  buckets: readonly YearBucket[];
  series: readonly BarSeries[];
  /** Stack series (signed) or place them side by side within each bucket. */
  mode?: 'stacked' | 'grouped';
}

/** Bars per calendar-year bucket, positioned on the shared time axis. */
export function BarLayer({ buckets, series, mode = 'stacked' }: BarLayerProps) {
  const { dates, xScale, yScale } = useChart();
  const bands = useMemo(
    () =>
      mode === 'stacked'
        ? stackSigned(
            series.map((s) => s.values),
            buckets.length,
          )
        : null,
    [mode, series, buckets.length],
  );
  return (
    <g aria-hidden>
      {buckets.map((bucket, b) => {
        const start = dates[bucket.from];
        const end = dates[bucket.to];
        if (!start || !end) return null;
        const x0 = xScale(start);
        const x1 = xScale(end);
        const slot = Math.max(2, x1 - x0 + (x1 - x0) / Math.max(1, bucket.to - bucket.from));
        const pad = Math.min(6, slot * 0.18);
        const width = Math.max(1, slot - pad * 2);
        return (
          <g key={bucket.year}>
            {series.map((s, i) => {
              let yTop: number;
              let yBottom: number;
              let x = x0 + pad;
              let w = width;
              if (bands) {
                const band = bands[i];
                const v0 = band?.y0[b] ?? 0;
                const v1 = band?.y1[b] ?? 0;
                yTop = yScale(Math.max(v0, v1));
                yBottom = yScale(Math.min(v0, v1));
              } else {
                const v = s.values[b] ?? 0;
                w = width / series.length;
                x = x0 + pad + w * i;
                yTop = yScale(Math.max(0, v));
                yBottom = yScale(Math.min(0, v));
              }
              const h = Math.max(0, yBottom - yTop);
              if (h === 0) return null;
              return (
                <rect
                  key={s.id}
                  x={x}
                  y={yTop}
                  width={Math.max(1, w - (bands ? 0 : 1))}
                  height={h}
                  rx={Math.min(2, w / 4)}
                  style={{
                    fill: s.color,
                    stroke: 'var(--chart-surface)',
                    opacity: s.muted ? 0.25 : 0.9,
                  }}
                  strokeWidth={0.75}
                />
              );
            })}
          </g>
        );
      })}
    </g>
  );
}

export interface BucketLineLayerProps {
  buckets: readonly YearBucket[];
  /** One value per bucket. */
  values: ArrayLike<number>;
  color: string;
}

/** A line with dots through the middle of each yearly bucket (e.g. net flow over bars). */
export function BucketLineLayer({ buckets, values, color }: BucketLineLayerProps) {
  const { dates, xScale, yScale } = useChart();
  const points = buckets.flatMap((b, i) => {
    const start = dates[b.from];
    const end = dates[b.to];
    if (!start || !end) return [];
    return [{ x: (xScale(start) + xScale(end)) / 2, y: yScale(values[i] ?? 0) }];
  });
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${String(p.x)},${String(p.y)}`).join('');
  return (
    <g aria-hidden>
      <path d={d} fill="none" style={{ stroke: color }} strokeWidth={2} strokeLinejoin="round" />
      {points.map((p) => (
        <circle
          key={p.x}
          cx={p.x}
          cy={p.y}
          r={3}
          style={{ fill: color, stroke: 'var(--chart-surface)' }}
          strokeWidth={1.5}
        />
      ))}
    </g>
  );
}

export interface BandLayerProps {
  /** Lower and upper edge of the band per month (e.g. P10 and P90). */
  lower: ArrayLike<number>;
  upper: ArrayLike<number>;
  color: string;
  /** Fill opacity; nest a wide light band under a narrower darker one for a fan. */
  opacity?: number;
}

/** A shaded range between two series: Monte Carlo percentile fans. */
export function BandLayer({ lower, upper, color, opacity = 0.18 }: BandLayerProps) {
  const { dates, xScale, yScale } = useChart();
  const indices = useVisibleIndices(Math.min(lower.length, upper.length));
  const d = useMemo(
    () =>
      area<number>({
        x: (i) => xScale(dates[i] as Date),
        y0: (i) => yScale(lower[i] as number),
        y1: (i) => yScale(upper[i] as number),
      })(indices) ?? '',
    [dates, xScale, yScale, lower, upper, indices],
  );
  return <path aria-hidden d={d} style={{ fill: color }} fillOpacity={opacity} />;
}

export interface DiffAreaProps {
  /** A difference series (e.g. variant − baseline). */
  values: ArrayLike<number>;
  positiveColor?: string;
  negativeColor?: string;
  opacity?: number;
}

/** Diverging fill: the part above zero in one colour, the part below zero in the other. */
export function DiffArea({
  values,
  positiveColor = 'var(--chart-diverging-pos)',
  negativeColor = 'var(--chart-diverging-neg)',
  opacity = 0.22,
}: DiffAreaProps) {
  const { dates, xScale, yScale } = useChart();
  const indices = useVisibleIndices(values.length);
  const paths = useMemo(() => {
    const part = (clamp: (v: number) => number): string =>
      area<number>({
        x: (i) => xScale(dates[i] as Date),
        y0: () => yScale(0),
        y1: (i) => yScale(clamp(values[i] as number)),
      })(indices) ?? '';
    return { above: part((v) => Math.max(0, v)), below: part((v) => Math.min(0, v)) };
  }, [dates, xScale, yScale, values, indices]);
  return (
    <g aria-hidden>
      <path d={paths.above} style={{ fill: positiveColor }} fillOpacity={opacity} />
      <path d={paths.below} style={{ fill: negativeColor }} fillOpacity={opacity} />
    </g>
  );
}

export interface EndLabel {
  id: string;
  /** Usually the series name, optionally with its last value ("Buy · €1.1m"). */
  text: string;
  values: ArrayLike<number>;
  color: string;
}

export interface EndLabelsProps {
  labels: readonly EndLabel[];
  fontSize?: number;
}

/**
 * Direct labels at the right end of lines, so series read without a legend, in grayscale and for
 * colour-blind readers. Labels are spread apart when lines end close together, with a leader from
 * the line's end. Text stays in the axis ink; the colour is carried by the key. Pass it as the
 * frame's `annotations` (it draws outside the plot) with a right margin of `endLabelsWidth`.
 */
export function EndLabels({ labels, fontSize = 11 }: EndLabelsProps) {
  const { yScale, innerWidth, innerHeight, range } = useChart();
  const ends = labels.map((l) => yScale(l.values[range[1]] ?? 0));
  const placed = spreadLabels(ends, fontSize * 1.3, 0, innerHeight);
  const x = innerWidth + END_LABEL_GAP;
  return (
    <g aria-hidden>
      {labels.map((l, i) => {
        const y0 = ends[i] ?? 0;
        const y = placed[i] ?? y0;
        return (
          <g key={l.id}>
            <path
              d={`M${String(innerWidth)},${String(y0)} L${String(x)},${String(y)} h${String(END_LABEL_KEY)}`}
              fill="none"
              style={{ stroke: l.color }}
              strokeWidth={1.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            <text
              x={x + END_LABEL_KEY + 4}
              y={y}
              dy="0.32em"
              style={{ fill: 'var(--chart-axis-label)', fontSize }}
            >
              {l.text}
            </text>
          </g>
        );
      })}
    </g>
  );
}
